import { useRef, useState } from "react";
import { MessagesSquare, ThumbsUp, AlertTriangle, Lightbulb, ShieldAlert, Lock, Send, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { KENTRO } from "@/lib/constants";
import { readUtms } from "@/lib/lead";
import { checkAntispam, markAntispamSubmitted } from "@/lib/ouvidoria-antispam";
import { useLocale } from "@/i18n/LocaleProvider";
import type { Locale } from "@/i18n/types";

type TypeId = "elogio" | "reclamacao" | "sugestao" | "denuncia";

const TYPE_DEFS: { id: TypeId; icon: typeof ThumbsUp }[] = [
  { id: "elogio", icon: ThumbsUp },
  { id: "reclamacao", icon: AlertTriangle },
  { id: "sugestao", icon: Lightbulb },
  { id: "denuncia", icon: ShieldAlert },
];

const CONTENT: Record<Locale, {
  badge: string;
  titleHi: string; titleAfter: string;
  lead: React.ReactNode;
  typesLegend: string;
  typeLabels: Record<TypeId, string>;
  anonLabel: string;
  fieldName: string; fieldNamePh: string;
  fieldEmail: string; fieldEmailPh: string;
  fieldPhone: string; fieldPhonePh: string;
  fieldSubject: string; fieldSubjectPh: string;
  fieldMessage: string;
  msgPhDenuncia: string;
  msgPhDefault: string;
  msgHint: string;
  submit: string; submitting: string;
  successTitle: string; successDesc: string;
  errorMsg: string;
  asideHowTitle: string;
  asideHowSteps: string[];
  asideChannelsTitle: string;
  asideSigiloTitle: string;
  asideSigiloBody: React.ReactNode;
  waLabel: string;
  captchaLabel: string;
  captchaError: string;
  emptyFallback: string;
}> = {
  pt: {
    badge: "Canal direto com a administração",
    titleHi: "Canal de Denúncias", titleAfter: " Kentro",
    lead: <>Este espaço é um <strong className="text-foreground">canal de denúncias</strong> e comunicação direta entre colaboradores, parceiros e participantes da Kentro Sistemas com a administração da organização. Utilize o formulário abaixo para registrar <strong className="text-foreground">elogios</strong>, <strong className="text-foreground">reclamações</strong>, <strong className="text-foreground">sugestões</strong> ou <strong className="text-foreground">denúncias</strong> — o registro pode ser feito de forma anônima.</>,
    typesLegend: "Qual o tipo da sua manifestação?",
    typeLabels: { elogio: "Elogio", reclamacao: "Reclamação", sugestao: "Sugestão", denuncia: "Denúncia" },
    anonLabel: "Enviar de forma anônima",
    fieldName: "Nome", fieldNamePh: "Como podemos te chamar",
    fieldEmail: "E-mail", fieldEmailPh: "voce@empresa.com",
    fieldPhone: "Telefone", fieldPhonePh: "(00) 00000-0000",
    fieldSubject: "Assunto", fieldSubjectPh: "Resumo da sua manifestação",
    fieldMessage: "Mensagem",
    msgPhDenuncia: "Sugerimos que a denúncia seja rica em detalhes para facilitar a análise — cite dados, nomes e datas da ocorrência.",
    msgPhDefault: "Descreva sua manifestação com o máximo de detalhes possível.",
    msgHint: "As manifestações serão analisadas com celeridade. Quando houver necessidade, ofereceremos resposta adequada às questões apresentadas.",
    submit: "Enviar manifestação", submitting: "Enviando…",
    successTitle: "Manifestação registrada",
    successDesc: "Recebemos sua mensagem. Quando necessário, responderemos com celeridade.",
    errorMsg: "Não foi possível enviar agora. Tente novamente em instantes.",
    asideHowTitle: "Como funciona",
    asideHowSteps: [
      "Você registra sua manifestação — de forma identificada ou anônima.",
      "A administração analisa o caso com confidencialidade.",
      "Quando houver necessidade e contato informado, você recebe uma resposta.",
    ],
    asideChannelsTitle: "Outros canais",
    asideSigiloTitle: "Sigilo e proteção",
    asideSigiloBody: <>As manifestações recebidas por este canal são tratadas com confidencialidade. Quando identificada, seus dados pessoais são tratados conforme nossa <a className="text-primary-glow hover:underline" href="/privacidade">Política de Privacidade</a> e o <a className="text-primary-glow hover:underline" href="/aviso-de-privacidade">Aviso de Privacidade (LGPD)</a>.</>,
    waLabel: "Olá, gostaria de falar com o Canal de Denúncias da Kentro.",
    captchaLabel: "Confirmo que sou humano e as informações são verdadeiras.",
    captchaError: "Confirme que você é humano antes de enviar.",
    emptyFallback: "(não informado)",
  },
  en: {
    badge: "Direct channel to management",
    titleHi: "Reporting Channel", titleAfter: " — Kentro",
    lead: <>This is the Kentro Sistemas <strong className="text-foreground">reporting channel</strong> for employees, partners and participants. Use the form below to send <strong className="text-foreground">compliments</strong>, <strong className="text-foreground">complaints</strong>, <strong className="text-foreground">suggestions</strong> or <strong className="text-foreground">reports</strong> — the submission can be anonymous.</>,
    typesLegend: "What type of message is this?",
    typeLabels: { elogio: "Compliment", reclamacao: "Complaint", sugestao: "Suggestion", denuncia: "Report" },
    anonLabel: "Send anonymously",
    fieldName: "Name", fieldNamePh: "What should we call you",
    fieldEmail: "Email", fieldEmailPh: "you@company.com",
    fieldPhone: "Phone", fieldPhonePh: "+1 555 000 0000",
    fieldSubject: "Subject", fieldSubjectPh: "Summary of your message",
    fieldMessage: "Message",
    msgPhDenuncia: "We suggest reports include rich detail to help analysis — cite data, names and dates of the event.",
    msgPhDefault: "Describe your message with as much detail as possible.",
    msgHint: "Messages will be analyzed promptly. When needed, we'll provide an appropriate response to the issues raised.",
    submit: "Send message", submitting: "Sending…",
    successTitle: "Message recorded",
    successDesc: "We received your message. When needed, we'll respond promptly.",
    errorMsg: "We couldn't send right now. Please try again in a moment.",
    asideHowTitle: "How it works",
    asideHowSteps: [
      "You record your message — identified or anonymously.",
      "Management reviews the case with confidentiality.",
      "When needed and contact is provided, you receive a response.",
    ],
    asideChannelsTitle: "Other channels",
    asideSigiloTitle: "Confidentiality and protection",
    asideSigiloBody: <>Messages received through this channel are treated with confidentiality. When identified, your personal data is handled per our <a className="text-primary-glow hover:underline" href="/en/privacidade">Privacy Policy</a> and the <a className="text-primary-glow hover:underline" href="/en/aviso-de-privacidade">Privacy Notice (LGPD)</a>.</>,
    waLabel: "Hello, I would like to speak with the Kentro Reporting Channel.",
    captchaLabel: "I confirm I am human and the information is truthful.",
    captchaError: "Please confirm you are human before submitting.",
    emptyFallback: "(not provided)",
  },
  es: {
    badge: "Canal directo con la administración",
    titleHi: "Canal de Denuncias", titleAfter: " Kentro",
    lead: <>Este es el <strong className="text-foreground">canal de denuncias</strong> de Kentro Sistemas para colaboradores, socios y participantes. Utiliza el formulario de abajo para registrar <strong className="text-foreground">elogios</strong>, <strong className="text-foreground">reclamos</strong>, <strong className="text-foreground">sugerencias</strong> o <strong className="text-foreground">denuncias</strong> — el registro puede hacerse de forma anónima.</>,
    typesLegend: "¿Cuál es el tipo de tu manifestación?",
    typeLabels: { elogio: "Elogio", reclamacao: "Reclamo", sugestao: "Sugerencia", denuncia: "Denuncia" },
    anonLabel: "Enviar de forma anónima",
    fieldName: "Nombre", fieldNamePh: "¿Cómo podemos llamarte?",
    fieldEmail: "E-mail", fieldEmailPh: "tu@empresa.com",
    fieldPhone: "Teléfono", fieldPhonePh: "+00 000 000 000",
    fieldSubject: "Asunto", fieldSubjectPh: "Resumen de tu manifestación",
    fieldMessage: "Mensaje",
    msgPhDenuncia: "Sugerimos que la denuncia sea rica en detalles para facilitar el análisis — cita datos, nombres y fechas del hecho.",
    msgPhDefault: "Describe tu manifestación con el máximo de detalles posible.",
    msgHint: "Las manifestaciones se analizarán con celeridad. Cuando sea necesario, ofreceremos una respuesta adecuada a las cuestiones presentadas.",
    submit: "Enviar manifestación", submitting: "Enviando…",
    successTitle: "Manifestación registrada",
    successDesc: "Recibimos tu mensaje. Cuando sea necesario, responderemos con celeridad.",
    errorMsg: "No fue posible enviar ahora. Inténtalo de nuevo en instantes.",
    asideHowTitle: "Cómo funciona",
    asideHowSteps: [
      "Registras tu manifestación — identificada o anónima.",
      "La administración analiza el caso con confidencialidad.",
      "Cuando sea necesario y haya contacto informado, recibes una respuesta.",
    ],
    asideChannelsTitle: "Otros canales",
    asideSigiloTitle: "Confidencialidad y protección",
    asideSigiloBody: <>Las manifestaciones recibidas por este canal se tratan con confidencialidad. Cuando estés identificado, tus datos personales se tratan conforme a nuestra <a className="text-primary-glow hover:underline" href="/es/privacidade">Política de Privacidad</a> y el <a className="text-primary-glow hover:underline" href="/es/aviso-de-privacidade">Aviso de Privacidad (LGPD)</a>.</>,
    waLabel: "Hola, me gustaría hablar con el Canal de Denuncias de Kentro.",
    captchaLabel: "Confirmo que soy humano y la información es verdadera.",
    captchaError: "Confirma que eres humano antes de enviar.",
    emptyFallback: "(no informado)",
  },
};

const ANTISPAM_MSGS: Record<Locale, {
  blocked: string;
  cooldown: (s: number) => string;
  duplicate: string;
  httpError: (s: number) => string;
  httpErrorTitle: string;
  networkTitle: string;
  networkDesc: string;
  timeoutTitle: string;
  timeoutDesc: string;
}> = {
  pt: {
    blocked: "Envio bloqueado por proteção anti-spam. Tente novamente.",
    cooldown: (s) => `Aguarde ${s}s antes de enviar outra manifestação.`,
    duplicate: "Esta manifestação já foi registrada recentemente.",
    httpErrorTitle: "Não foi possível registrar sua manifestação",
    httpError: (s) => `O servidor respondeu com o status ${s}. Tente novamente em instantes ou use os canais alternativos abaixo.`,
    networkTitle: "Falha de conexão ao enviar manifestação",
    networkDesc: "Não conseguimos contatar o servidor. Verifique sua conexão ou use o e-mail/telefone abaixo.",
    timeoutTitle: "Tempo de envio excedido",
    timeoutDesc: "O envio demorou demais. Verifique sua conexão e tente novamente.",
  },
  en: {
    blocked: "Submission blocked by anti-spam protection. Please try again.",
    cooldown: (s) => `Please wait ${s}s before sending another message.`,
    duplicate: "This message was already submitted recently.",
    httpErrorTitle: "We couldn't record your message",
    httpError: (s) => `The server replied with status ${s}. Please try again shortly or use the channels below.`,
    networkTitle: "Network error while sending",
    networkDesc: "We couldn't reach the server. Check your connection or use the email/phone below.",
    timeoutTitle: "Request timed out",
    timeoutDesc: "The submission took too long. Check your connection and try again.",
  },
  es: {
    blocked: "Envío bloqueado por protección anti-spam. Inténtalo de nuevo.",
    cooldown: (s) => `Espera ${s}s antes de enviar otra manifestación.`,
    duplicate: "Esta manifestación ya fue registrada recientemente.",
    httpErrorTitle: "No pudimos registrar tu manifestación",
    httpError: (s) => `El servidor respondió con el estado ${s}. Inténtalo en unos instantes o usa los canales alternativos abajo.`,
    networkTitle: "Error de conexión al enviar",
    networkDesc: "No pudimos contactar al servidor. Verifica tu conexión o usa el e-mail/teléfono abajo.",
    timeoutTitle: "Tiempo de envío agotado",
    timeoutDesc: "El envío tardó demasiado. Verifica tu conexión e inténtalo de nuevo.",
  },
};

export function OuvidoriaPage() {
  const locale = useLocale();
  const c = CONTENT[locale];
  const a = ANTISPAM_MSGS[locale];
  const [type, setType] = useState<TypeId>("reclamacao");
  const [anon, setAnon] = useState(false);
  const [sending, setSending] = useState(false);
  const [humanConfirmed, setHumanConfirmed] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const formOpenedAtRef = useRef<number>(Date.now());

  const sigiloBody = c.asideSigiloBody;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const assunto = ((fd.get("assunto") as string) || "").trim();
    const mensagem = ((fd.get("mensagem") as string) || "").trim();
    const honeypot = (fd.get("website") as string) || "";

    if (!humanConfirmed) {
      setCaptchaError(true);
      toast.error(c.captchaError);
      return;
    }

    const spam = await checkAntispam({
      honeypot,
      formOpenedAt: formOpenedAtRef.current,
      contentKey: `${type}|${assunto}|${mensagem}`,
    });
    if (!spam.ok) {
      if (spam.reason === "cooldown") toast.error(a.cooldown(spam.retryInSec ?? 60));
      else if (spam.reason === "duplicate") toast.error(a.duplicate);
      else toast.error(a.blocked);
      return;
    }

    setSending(true);
    const utms = readUtms();
    const fb = c.emptyFallback;
    const orNA = (v: string) => (v && v.trim()) ? v.trim() : fb;
    const nome = anon ? "Anônimo" : orNA((fd.get("nome") as string) || "");
    const email = anon ? "" : orNA((fd.get("email") as string) || "");
    const telefone = anon ? "" : orNA((fd.get("telefone") as string) || "");
    const assuntoSafe = assunto || fb;
    const mensagemSafe = mensagem || fb;
    const tipoLabel = c.typeLabels[type];

    const resumo = [
      `[Canal de Denúncias] ${tipoLabel}`,
      `Tipo: ${tipoLabel}`,
      `Anônimo: ${anon ? "Sim" : "Não"}`,
      `Nome: ${nome}`,
      `E-mail: ${email || fb}`,
      `Telefone: ${telefone || fb}`,
      `Assunto: ${assuntoSafe}`,
      `Mensagem: ${mensagemSafe}`,
      `Idioma: ${locale}`,
      `Data: ${new Date().toISOString()}`,
    ].join("\n");

    const payload = {
      origem: "ouvidoria",
      tipo: type,
      tipo_label: tipoLabel,
      anonimo: anon,
      nome,
      email,
      telefone,
      assunto: assuntoSafe,
      mensagem: mensagemSafe,
      resumo,
      confirmado_humano: true,
      data: new Date().toISOString(),
      locale,
      ...utms,
      capture_page_url: typeof window !== "undefined" ? window.location.href : "",
      capture_pathname: typeof window !== "undefined" ? window.location.pathname : "",
      capture_form_id: "kentro-ouvidoria",
    };
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15_000);
      const res = await fetch("/api/public/ouvidoria", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        toast.error(a.httpErrorTitle, { description: a.httpError(res.status) });
        return;
      }

      markAntispamSubmitted(spam.hash);
      toast.success(c.successTitle, { description: c.successDesc });
      form.reset();
      setType("reclamacao");
      setAnon(false);
      setHumanConfirmed(false);
      setCaptchaError(false);
      formOpenedAtRef.current = Date.now();
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === "AbortError";
      toast.error(aborted ? a.timeoutTitle : a.networkTitle, {
        description: aborted ? a.timeoutDesc : a.networkDesc,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <MessagesSquare className="size-3.5" /> {c.badge}
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            <span className="gradient-text">{c.titleHi}</span>{c.titleAfter}
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground">{c.lead}</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.4fr_1fr] lg:px-8">
        <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <fieldset>
            <legend className="text-sm font-semibold">{c.typesLegend}</legend>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {TYPE_DEFS.map((t) => {
                const Icon = t.icon;
                const active = type === t.id;
                return (
                  <button key={t.id} type="button" onClick={() => setType(t.id)} aria-pressed={active}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-xs font-medium transition ${active ? "border-primary-glow/60 bg-primary/10 text-foreground shadow-glow" : "border-border bg-background/40 text-muted-foreground hover:bg-foreground/5"}`}>
                    <Icon className="size-4" />
                    {c.typeLabels[t.id]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Honeypot anti-spam */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="website">Do not fill this field</label>
            <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
          </div>


          <div className="mt-6 flex items-center gap-2 rounded-xl border border-border bg-background/40 p-3">
            <input id="anon" type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} className="size-4 accent-[color:var(--primary-glow)]" />
            <label htmlFor="anon" className="flex items-center gap-2 text-sm">
              <Lock className="size-4 text-primary-glow" />
              {c.anonLabel}
            </label>
          </div>

          {!anon && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field name="nome" label={c.fieldName} placeholder={c.fieldNamePh} required />
              <Field name="email" type="email" label={c.fieldEmail} placeholder={c.fieldEmailPh} required />
              <Field name="telefone" type="tel" label={c.fieldPhone} placeholder={c.fieldPhonePh} required />
            </div>
          )}

          <div className="mt-4">
            <Field name="assunto" label={c.fieldSubject} placeholder={c.fieldSubjectPh} required />
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium" htmlFor="mensagem">{c.fieldMessage}</label>
            <textarea id="mensagem" name="mensagem" rows={6} required
              placeholder={type === "denuncia" ? c.msgPhDenuncia : c.msgPhDefault}
              className="mt-1.5 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary-glow/60 focus:outline-none focus:ring-2 focus:ring-primary-glow/30" />
            <p className="mt-2 text-xs text-muted-foreground">{c.msgHint}</p>
          </div>

          <div className={`mt-6 flex items-start gap-2 rounded-xl border p-3 transition ${captchaError && !humanConfirmed ? "border-destructive bg-destructive/5" : "border-border bg-background/40"}`}>
            <input
              id="human-check"
              type="checkbox"
              checked={humanConfirmed}
              onChange={(e) => { setHumanConfirmed(e.target.checked); if (e.target.checked) setCaptchaError(false); }}
              className="mt-0.5 size-4 accent-[color:var(--primary-glow)]"
              aria-invalid={captchaError && !humanConfirmed}
              aria-describedby={captchaError && !humanConfirmed ? "human-check-error" : undefined}
            />
            <label htmlFor="human-check" className="flex items-center gap-2 text-sm">
              <ShieldAlert className="size-4 text-primary-glow" />
              {c.captchaLabel}
            </label>
          </div>
          {captchaError && !humanConfirmed && (
            <p id="human-check-error" className="mt-2 text-xs text-destructive">{c.captchaError}</p>
          )}

          <button type="submit" disabled={sending || !humanConfirmed}
            className="gradient-primary shadow-glow mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-60 sm:w-auto">
            <Send className="size-4" />
            {sending ? c.submitting : c.submit}
          </button>
        </form>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-border bg-card/40 p-6">
            <h2 className="text-base font-semibold">{c.asideHowTitle}</h2>
            <ul className="mt-3 space-y-3 text-sm text-muted-foreground">
              {c.asideHowSteps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary-glow">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card/40 p-6">
            <h2 className="text-base font-semibold">{c.asideChannelsTitle}</h2>
            <ul className="mt-3 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Mail className="size-4" />
                <a className="hover:text-foreground" href={`mailto:${KENTRO.email}`}>{KENTRO.email}</a>
              </li>
              {KENTRO.phones.map((p) => {
                const href =
                  p.tel === "553832208706" || p.tel === "08005502020" || p.tel === "5511915827716"
                    ? KENTRO.whatsappComercialUrl
                    : p.whatsapp
                      ? KENTRO.whatsappUrl(c.waLabel)
                      : `tel:+${p.tel.replace(/\D/g, "")}`;
                return (
                  <li key={p.tel} className="flex items-center gap-2">
                    <Phone className="size-4" />
                    <a href={href} target={p.whatsapp ? "_blank" : undefined} rel={p.whatsapp ? "noopener noreferrer" : undefined} className="hover:text-foreground">
                      <span className="text-foreground/80">{p.label}:</span> {p.display}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card/40 p-6">
            <h2 className="text-base font-semibold">{c.asideSigiloTitle}</h2>
            <p className="mt-3 text-sm text-muted-foreground">{sigiloBody}</p>
          </div>
        </aside>
      </section>
    </>
  );
}

function Field({ name, label, type = "text", placeholder, required }: { name: string; label: string; type?: string; placeholder?: string; required?: boolean }) {
  return (
    <div>
      <label className="text-sm font-medium" htmlFor={name}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input id={name} name={name} type={type} required={required} placeholder={placeholder}
        className="mt-1.5 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary-glow/60 focus:outline-none focus:ring-2 focus:ring-primary-glow/30" />
    </div>
  );
}
